import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  AutocompleteProps,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { DropdownOption } from 'components/atoms/Dropdown/Dropdown';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import ImpactIcon from 'components/StudyDashboard/QCMarkers/ImpactIcon';
import { find, includes, map } from 'lodash';
import React from 'react';

import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useChannelsOptionsWithAll } from 'utils/queryHooks/useChannelsOptions';
import { useCasesParams } from 'utils/useCasesParams';
import useStudy from 'utils/useStudy';

interface StainTypeSelectDropdownProps
  extends Omit<
    AutocompleteProps<DropdownOption, true, false, false>,
    | 'onChange'
    | 'value'
    | 'options'
    | 'multiple'
    | 'loading'
    | 'renderInput'
    | 'getOptionLabel'
    | 'renderOption'
    | 'renderTags'
  > {
  selectedStainTypeIds: string[];
  onSelectStainTypes: (stainTypeIds: string[]) => void;
  studyId: string;
  isGlobal?: boolean;
}

const icon = <CheckBoxOutlineBlankIcon />;
const checkedIcon = <CheckBoxIcon />;

const StainTypeSelectDropdown: React.FC<StainTypeSelectDropdownProps> = ({
  selectedStainTypeIds,
  onSelectStainTypes,
  studyId,
  isGlobal = false,
  ...autocompleteProps
}) => {
  const {
    stainTypeOptions,
    stainTypeOptionsWithoutDeprecated,
    mifMarkerTypeOptionsWithoutDeprecated,
    isLoadingStainTypeOptions,
  } = useStainTypeOptions();
  const theme = useTheme();

  const isSpecificStudySelected = studyId && !isGlobal && !includes(NON_SPECIFIC_STUDY_VALUES, studyId.toString());
  const { study } = useStudy(studyId, { enabled: isSpecificStudySelected });
  const qcMarkersStudySettings = study?.settings?.qcMarkersSettings;

  const { casesParams } = useCasesParams();
  const { channelsOptionsWithAllOption, isLoadingChannelMarkers } = useChannelsOptionsWithAll({
    casesParams,
    currentChannels: selectedStainTypeIds,
  });

  const channelsOptionsForGlobal = map(mifMarkerTypeOptionsWithoutDeprecated, (stainType) => ({
    value: stainType.id,
    text: stainType.displayName,
  }));

  const channelsOptions = isGlobal ? channelsOptionsForGlobal : channelsOptionsWithAllOption;
  const stainTypesFilteredIds = map(channelsOptions, 'value');

  return (
    <FormControl
      sx={{
        width: '100%',
      }}
    >
      <Autocomplete
        multiple
        loading={isLoadingStainTypeOptions || isLoadingChannelMarkers}
        getOptionLabel={(option) => option?.text}
        options={channelsOptions}
        value={map(
          selectedStainTypeIds,
          (channelId) =>
            find(channelsOptions, { value: channelId }) || {
              value: channelId,
              text: channelId,
            }
        )}
        disableCloseOnSelect
        onChange={(event, newValue) => onSelectStainTypes(map(newValue, 'value'))}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderOption={(props, option, { selected }) => {
          return (
            <li {...props} key={option.value}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
                size="small"
              />
              <Grid container alignContent="center">
                <Grid item>
                  <ImpactIcon qcImpactId={qcMarkersStudySettings?.[option.value] || ''} />
                </Grid>
                <Grid item>{option.text}</Grid>
              </Grid>
            </li>
          );
        }}
        renderInput={(params) => <TextField {...params} label="Marker(s)" />}
        renderTags={(value, getTagProps) =>
          map(value, (option, index) => {
            const isLoading = isLoadingStainTypeOptions || isLoadingChannelMarkers;
            const isOptionValid = isLoading || includes(stainTypesFilteredIds, option.value);
            return (
              <Tooltip key={option.value} title={!isOptionValid && !isLoading && 'not a mif marker'}>
                <Chip
                  sx={{
                    backgroundColor: isOptionValid
                      ? 'auto'
                      : theme.palette.mode === 'light'
                      ? theme.palette.error.light
                      : theme.palette.error.dark,
                  }}
                  variant="filled"
                  label={
                    <Grid container alignContent="center" {...getTagProps({ index })}>
                      <Grid item>
                        <ImpactIcon qcImpactId={qcMarkersStudySettings?.[option.value] || ''} />
                      </Grid>
                      <Grid item>{option.text}</Grid>
                    </Grid>
                  }
                  {...getTagProps({ index })}
                />
              </Tooltip>
            );
          })
        }
        {...autocompleteProps}
      />
    </FormControl>
  );
};

export default StainTypeSelectDropdown;
